/**
 * Schul-Statistik (V-16): Notenverlauf je Fach seit Beginn des Schuljahres,
 * Lernzeit neben dem Schnitt und die Klausuren der naechsten Wochen.
 */
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { FileText } from 'lucide-react';

import { db } from '@/db/schema';
import { klausurDatum } from '@/domain/school/exams';
import { dauerText } from '@/domain/school/studyTime';
import { lernzeitGegenNote, notenVerlauf, schuljahrBeginn, type FachZeile } from '@/domain/stats/schule';
import { pfad } from '@/app/fokus';
import { today } from '@/lib/datetime';
import { LinienDiagramm } from '@/ui/composites/LinienDiagramm';
import { PageGrid } from '@/ui/composites/Page';
import { Erkenntnis, StatKarte } from '@/ui/composites/Statistik';
import { Tabelle } from '@/ui/composites/Tabelle';
import { ZeitleisteWochen } from '@/ui/composites/ZeitleisteWochen';
import type { CalendarEvent, Grade, LocalDate, Notensystem, StudySession, Subject } from '@/domain/types';

const zahl = (v: number) => v.toLocaleString('de-DE', { maximumFractionDigits: 1 });

export function NotenVerlaufDiagramm({ noten, faecher, system, von, bis }: { noten: Grade[]; faecher: Subject[]; system: Notensystem; von: LocalDate; bis: LocalDate }) {
  const v = notenVerlauf(noten, faecher, { von, bis }, system);
  if (!v.reihen.length) return <p className="text-[12px] text-ink-faint">Noch keine Noten in diesem Zeitraum.</p>;
  const punkte = system === 'punkte';
  return (
    <>
      <LinienDiagramm titel={`Notenschnitt je Fach (${punkte ? 'Punkte 0–15' : 'Noten 1–6, 1 oben'})`} min={punkte ? 0 : 1} max={punkte ? 15 : 6}
        ticks={punkte ? [0, 5, 10, 15] : [1, 2, 3, 4, 5, 6]} einheit={punkte ? ' P' : ''} umgekehrt={!punkte}
        punkte={v.abschnitte.map((a) => ({ key: a.key, titel: a.titel, kurz: a.kurz }))}
        reihen={v.reihen.map((r, i) => ({ id: r.fach.id, name: r.fach.name, platz: i, farbe: r.fach.color, werte: r.werte }))} />
      <p className="text-[11px] text-ink-faint">Gewichteter Schnitt aller Noten bis zum Ende des jeweiligen Abschnitts.</p>
    </>
  );
}

export function SchulStatistik({ faecher, noten, system }: { faecher: Subject[]; noten: Grade[]; system: Notensystem }) {
  const heute = today();
  const von = schuljahrBeginn(heute);
  const sitzungen = useLiveQuery(() => db.studySessions.toArray(), [], [] as StudySession[]);
  const klausuren = useLiveQuery(() => db.events.filter((e) => !!e.klausur).toArray(), [], [] as CalendarEvent[]);
  const zeilen = lernzeitGegenNote(sitzungen, noten, faecher, { von, bis: heute }, system, new Date());
  const meist = [...zeilen].sort((a, b) => b.minuten - a.minuten)[0];
  const farbe = new Map(faecher.map((f) => [f.id, f.color]));
  const punkte = klausuren.map((k) => ({
    key: k.id, datum: klausurDatum(k), titel: k.title, art: 'Klausur', icon: FileText,
    to: `/kalender?termin=${encodeURIComponent(`${k.id}::${k.start}`)}&datum=${klausurDatum(k)}`, farbe: k.subjectId ? farbe.get(k.subjectId) : undefined, erledigt: klausurDatum(k) < heute,
  })).filter((p) => p.datum >= heute);

  return (
    <PageGrid layout="zwei">
      <StatKarte titel="Notenverlauf" frage="Wie entwickeln sich meine Noten seit Beginn des Schuljahres?"
        aktion={<Link to="/statistik#schule" className="text-[12px] text-accent-text hover:underline">Mehr in der Statistik</Link>}>
        <NotenVerlaufDiagramm noten={noten} faecher={faecher} system={system} von={von} bis={heute} />
      </StatKarte>
      <div className="flex min-w-0 flex-col gap-5">
        <StatKarte titel="Lernzeit und Note" frage="Lerne ich dort, wo ich es brauche? Lernzeit und Schnitt seit 1. August.">
          <Tabelle<FachZeile> label="Lernzeit und Note je Fach" zeilen={zeilen} zeilenKey={(z) => z.fach.id} csv="lernzeit-und-noten" start={{ key: 'minuten', richtung: 'ab' }} leer="Noch keine Fächer."
            spalten={[
              { key: 'fach', titel: 'Fach', wert: (z) => z.fach.name, zelle: (z) => (
                <Link to={pfad.fach(z.fach.id)} className="flex items-center gap-1.5 hover:text-accent-text"><span className="size-2 rounded-full" style={{ backgroundColor: z.fach.color }} />{z.fach.name}</Link>
              ) },
              { key: 'minuten', titel: 'Lernzeit', rechts: true, wert: (z) => z.minuten, zelle: (z) => (z.minuten ? dauerText(z.minuten) : <span className="text-ink-faint">–</span>) },
              { key: 'schnitt', titel: system === 'punkte' ? 'Schnitt (P)' : 'Schnitt', rechts: true, wert: (z) => (system === 'punkte' ? z.punkte : z.schnitt), zelle: (z) => (z.schnitt === null ? <span className="text-ink-faint">–</span> : zahl(z.schnitt)) },
              { key: 'noten', titel: 'Noten', rechts: true, wert: (z) => z.noten },
            ]} />
          <Erkenntnis>{meist && meist.minuten > 0 ? `Am meisten gelernt: ${meist.fach.name} (${dauerText(meist.minuten)})${meist.schnitt !== null ? `, Schnitt ${zahl(meist.schnitt)}` : ''}.` : null}</Erkenntnis>
        </StatKarte>
        <StatKarte titel="Klausuren" frage="Welche Klausuren stehen in den nächsten 12 Wochen an?">
          {punkte.length ? <ZeitleisteWochen punkte={punkte} start={heute} wochen={12} leereWochen={false} /> : <p className="text-[12px] text-ink-faint">Keine Klausur angesetzt.</p>}
        </StatKarte>
      </div>
    </PageGrid>
  );
}
